<template>
	<section ref="sectionRef" class="trust-section">
		<div class="trust-section__header">
			<div class="trust-section__title">{{ t(`${I18N_NS}.index.subBanner.title`) }}</div>
			<div class="trust-section__subtitle">{{ t(`${I18N_NS}.index.subBanner.subtitle`) }}</div>
		</div>

		<div class="trust-section__review">
			<div class="trust-section__review-top">
				<div class="trust-section__name">
					{{ evaluateData[0]?.user_name || t(`${I18N_NS}.index.subBanner.defaultUserName`) }}
				</div>
				<div
					class="trust-section__stars"
					role="img"
					:aria-label="t(`${I18N_NS}.index.subBanner.starRatingAria`, { score: displayScore })"
				>
					<img
						v-for="v in displayStarCount"
						:key="`star-${v}`"
						:src="ratingStar"
						:alt="t(`${I18N_NS}.index.subBanner.starAlt`)"
					/>
					<img
						v-for="v in 5 - displayStarCount"
						:key="`empty-star-${v}`"
						:src="emptyStar"
						:alt="t(`${I18N_NS}.index.subBanner.emptyStarAlt`)"
					/>
					<span class="trust-section__score">{{ displayScore }}</span>
				</div>
			</div>
			<p class="trust-section__content">{{ evaluateData[0]?.content }}</p>
		</div>

		<div class="trust-section__stats">
			<div
				v-for="(item, index) in tagList"
				:key="item.title"
				class="trust-section__stat"
				:class="`trust-section__stat--${index + 1}`"
			>
				<img class="trust-section__stat-icon" :src="item.icon" :alt="item.desc" />
				<div class="trust-section__stat-info">
					<div class="trust-section__stat-title">{{ item.title }}</div>
					<div class="trust-section__stat-desc">{{ item.desc }}</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, ref } from "vue";
import { useI18n } from "~/composables/useI18n";
import { useMasterFortuneMobilePageData } from "@variants/seer/master-fortune/composables/useMasterFortunePageData";
import statUserIcon from "../../../pc/images/stat-user.png";
import statChartIcon from "../../../pc/images/stat-chart.png";
import statStarIcon from "../../../pc/images/stat-star.png";
import statGlobeIcon from "../../../pc/images/stat-globe.png";
import ratingStar from "../../../images/rating-star.png";
import emptyStar from "../../../pc/images/no_star.png";

const I18N_NS = "masterFortune";
const { t, trackTranslationDeps } = useI18n();
const pageData = useMasterFortuneMobilePageData();
const sectionRef = ref(null);

defineExpose({ sectionRef });

const evaluateData = computed(() => pageData.value?.evaluateList || []);

const displayScore = computed(() => {
	const score = Number(evaluateData.value?.[0]?.score);
	if (!score) return "5.0";
	return Math.min(score, 5).toFixed(1);
});

const displayStarCount = computed(() => {
	const score = Number(evaluateData.value?.[0]?.score);
	if (!score) return 5;
	return Math.min(Math.ceil(score), 5);
});

const tagList = computed(() => {
	trackTranslationDeps();
	return [
		{
			title: t(`${I18N_NS}.index.tags.0.title`),
			desc: t(`${I18N_NS}.index.tags.0.desc`),
			icon: statUserIcon,
		},
		{
			title: t(`${I18N_NS}.index.tags.1.title`),
			desc: t(`${I18N_NS}.index.tags.1.desc`),
			icon: statChartIcon,
		},
		{
			title: t(`${I18N_NS}.index.tags.2.title`),
			desc: t(`${I18N_NS}.index.tags.2.desc`),
			icon: statStarIcon,
		},
		{
			title: t(`${I18N_NS}.index.tags.3.title`),
			desc: t(`${I18N_NS}.index.tags.3.desc`),
			icon: statGlobeIcon,
		},
	];
});
</script>

<style lang="scss" scoped>
.trust-section {
	margin: 16px 32px 40px;
	padding: 24px;
	background: #ffffff;
	border: 1px solid #efefef;
	border-radius: 12px;

	&__header {
		text-align: center;
		background: linear-gradient(360deg, #ffffff 0%, #fffbf3 100%);
		border-bottom: 2px solid #efefef;
		border-radius: 8px 8px 0px 0px;
		padding: 32px;
	}

	&__title {
		font-weight: 700;
		font-size: 40px;
		line-height: 48px;
		color: #333333;
	}

	&__subtitle {
		margin-top: 12px;
		font-size: 32px;
		line-height: 36px;
		font-weight: 400;
		color: #f2af19;
	}

	&__review {
		margin-top: 20px;
	}

	&__review-top {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}

	&__name {
		font-weight: 700;
		font-size: 32px;
		line-height: 48px;
		color: #333333;
	}

	&__stars {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-shrink: 0;

		img {
			width: 32px;
			height: 32px;
		}
	}

	&__score {
		margin-left: 6px;
		font-size: 36px;
		line-height: 1;
		color: #595959;
	}

	&__content {
		margin: 32px 0 0;
		font-size: 28px;
		line-height: 48px;
		color: #333333;
		display: -webkit-box;
		-webkit-line-clamp: 6;
		line-clamp: 6;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__stats {
		margin-top: 32px;
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 30px;
	}

	&__stat {
		display: flex;
		align-items: flex-start;
		gap: 20px;
		padding: 16px 32px 16px 24px;
		border-radius: 8px 8px 0 0;
	}

	&__stat--1 {
		background: linear-gradient(to bottom, #feeaea 0%, #ffffff 100%);
	}

	&__stat--2 {
		background: linear-gradient(to bottom, #ecf3fe 0%, #ffffff 100%);
	}

	&__stat--3 {
		background: linear-gradient(to bottom, #fffaec 0%, #ffffff 100%);
	}

	&__stat--4 {
		background: linear-gradient(to bottom, #edfaee 0%, #ffffff 100%);
	}

	&__stat-icon {
		width: 32px;
		height: 32px;
		margin-top: 10px;
		flex-shrink: 0;
		object-fit: contain;
	}

	&__stat-title {
		font-weight: 600;
		font-size: 32px;
		line-height: 48px;
		color: #333333;
	}

	&__stat-desc {
		font-size: 24px;
		line-height: 36px;
		color: #333333;
	}
}
</style>
